import axios from 'axios';
import dayjs from 'dayjs';

import {
  ApprovalConversationEntry,
  ApprovalHeadline,
  ApprovalTicketStatus,
  BOOK_CANCEL_REFUSAL_STATUS,
  BookCancelPreview,
  BookCancelRefusal,
  BookCancelRefusalCode,
  BookRefundOption,
  BookShipTo,
  ITP_REFUSAL_STATUS,
  ItpRefusalCode,
  ItpTicketStatus,
  PRINT_FILE_REFUSAL_STATUS,
  PrintFileRefusalCode,
  RecipientAddress,
  SHIP_TO_INVALID,
  TicketApproval,
  TicketAction,
  TicketCancellation,
  TicketDetail,
  TicketEvent,
  TicketFileKind,
  TicketItp,
  TicketOrigin,
  TicketStatus,
  AnyTicketStatus,
  TICKET_STATUS_HELD,
} from './types';
import { formatMessage, printShopStrings } from './strings';

/**
 * Human label for a status, falling back to the raw value.
 *
 * Exported so callers holding an `AnyTicketStatus` can label it without
 * narrowing first — including `imported`, which has a label but is not a queue
 * status.
 */
export const statusText = (status: string): string =>
  printShopStrings.statusLabels[status as TicketStatus] ?? status;

/**
 * A paid ticket that has sat without operator action for too long. Drives the
 * queue's red SLA highlight (mockup: "Red bar = paid ticket with no action for
 * more than 2 days"). A ticket waiting for its ITP (itp-mvp) is under the same
 * rule: the ITP is the operator's to produce.
 */
export const SLA_THRESHOLD_DAYS = 2;

const SLA_STATUSES: readonly string[] = ['paid', 'itp_pending'];

export const isSlaBreach = (
  // Widened for the archive. An imported record is four years old by
  // definition, so an `ageDays` comparison would flag every single one — it was
  // never under an SLA, having been fulfilled before this system existed.
  ticket: { status: AnyTicketStatus; ageDays: number },
  thresholdDays: number = SLA_THRESHOLD_DAYS,
  // No archive branch is needed and none should be added: the check requires
  // `paid`, which an imported record is not. An `ageDays`-only rule WOULD have
  // flagged every imported record, since they are years old by definition.
): boolean =>
  SLA_STATUSES.includes(ticket.status) && ticket.ageDays >= thresholdDays;

/**
 * Pure helpers shared by the print-shop pages. Kept free of React so they can
 * be unit-tested in isolation.
 */

/** The status each action is allowed from (mirrors the DS state machine). */
export const ACTION_ALLOWED_FROM_STATUS: Record<TicketAction, TicketStatus> = {
  accept: 'paid',
  start_production: 'accepted',
  ship: 'in_production',
};

/** The status each action transitions the ticket to. */
export const ACTION_RESULT_STATUS: Record<TicketAction, TicketStatus> = {
  accept: 'accepted',
  start_production: 'in_production',
  ship: 'shipped',
};

/**
 * True for a historical record copied in from the book builder.
 *
 * The one thing that must follow from this: an imported ticket offers NO status
 * actions and no status correction. There is nothing to advance — the order was
 * fulfilled years ago — and nothing to correct it toward, because no system
 * records what it would be corrected to.
 */
export const isArchivedTicket = (status: string | undefined): boolean =>
  status === 'imported' || status === 'cancelled';

/**
 * True only for a record whose status could not be recovered.
 *
 * Separate from `isArchivedTicket` because the two say different things on
 * screen: a cancelled order is one we KNOW the ending of, and an imported one
 * is the opposite. Collapsing them would tell an operator "we do not know" about
 * 3,581 orders whose outcome is recorded.
 */
export const isUnknownStatusTicket = (status: string | undefined): boolean =>
  status === 'imported';

/**
 * Where a ticket lives.
 *
 * Exported so the queue and the lookup cannot disagree about it — they did:
 * the lookup navigated to `/print-shop/:orderId` while the route is
 * `/print-shop/tickets/:orderId`, so clicking a result changed the URL and
 * rendered nothing. No unit test caught it, because `useNavigate` is mocked
 * everywhere; it took a real browser.
 */
export const ticketPath = (orderId: string): string =>
  `/print-shop/tickets/${orderId}`;

/** Returns the single action available for a status, or null when terminal. */
export const actionForStatus = (status: TicketStatus): TicketAction | null => {
  const entry = Object.entries(ACTION_ALLOWED_FROM_STATUS).find(
    ([, allowedFrom]) => allowedFrom === status,
  );
  return entry ? (entry[0] as TicketAction) : null;
};

/**
 * Ordered milestones for the ticket detail stepper (task 42). "submitted" is a
 * presentational first node — a ticket only exists after the order was placed
 * and paid, so it is always complete; the real status enum starts at "paid".
 */
export const TICKET_STEP_ORDER = [
  'submitted',
  'paid',
  'accepted',
  'in_production',
  'shipped',
] as const;

/**
 * The stepper of an order whose plan includes an Instant Trust Package
 * (itp-mvp): it waits for the ITP between Submitted and Paid.
 */
export const ITP_TICKET_STEP_ORDER = [
  'submitted',
  'itp_pending',
  'paid',
  'accepted',
  'in_production',
  'shipped',
] as const;

export type TicketStep = (typeof ITP_TICKET_STEP_ORDER)[number];

export type StepState = 'done' | 'current' | 'todo';

/**
 * Resolves each stepper milestone's state from the ticket's current status:
 * milestones before the current one are `done`, the current one is `current`,
 * and later ones are `todo`. The current (and terminal `shipped`) milestone is
 * `current`, so it renders the filled "active" dot, not a checkmark — matching
 * the V2 mockup. An unknown status falls back to "submitted" being `current`.
 */
export const ticketStepStates = (
  status: TicketStatus,
  includesItp = false,
): { step: TicketStep; state: StepState }[] => {
  const order: readonly TicketStep[] = includesItp
    ? ITP_TICKET_STEP_ORDER
    : TICKET_STEP_ORDER;
  const currentIndex = order.indexOf(status as TicketStep);
  return order.map((step, index) => ({
    step,
    state:
      currentIndex < 0
        ? index === 0
          ? 'current'
          : 'todo'
        : index < currentIndex
          ? 'done'
          : index === currentIndex
            ? 'current'
            : 'todo',
  }));
};

/** Flowbite badge color for each ticket status chip. */
export const statusBadgeColor = (status: TicketStatus): string => {
  switch (status) {
    case 'awaiting_approval':
      return 'indigo';
    case 'changes_requested':
      return 'pink';
    case 'itp_pending':
      return 'teal';
    case 'paid':
      return 'warning';
    case 'accepted':
      return 'info';
    case 'in_production':
      return 'purple';
    case 'shipped':
      return 'success';
    default:
      return 'gray';
  }
};

/** Short display form of a Mongo order id (last 8 chars, uppercased). */
export const shortOrderId = (orderId: string): string =>
  orderId.length > 8 ? `…${orderId.slice(-8).toUpperCase()}` : orderId.toUpperCase();

/** Human-readable timestamp, e.g. `Jun 10, 2026 3:42 PM`. */
export const formatDateTime = (iso?: string): string =>
  iso ? dayjs(iso).format('MMM D, YYYY h:mm A') : '—';

/** Human-readable date, e.g. `Jun 10, 2026`. */
export const formatDate = (iso?: string): string =>
  iso ? dayjs(iso).format('MMM D, YYYY') : '—';

/** Single-line mailing address, e.g. `155 Broadway, New York, NY 10006`. */
export const formatRecipientAddress = (
  address: Partial<RecipientAddress>,
): string => {
  const cityState = [address.city, address.state].filter(Boolean).join(', ');
  const cityStateZip = [cityState, address.zip].filter(Boolean).join(' ');
  return (
    [address.address1, address.address2, cityStateZip]
      .filter((part) => part && part.trim() !== '')
      .join(', ') || '—'
  );
};

/** Full recipient name, falling back to a dash when GHL had neither. */
export const recipientName = (recipient: {
  firstName?: string;
  lastName?: string;
}): string =>
  `${recipient.firstName ?? ''} ${recipient.lastName ?? ''}`.trim() || '—';

/**
 * Copies text to the clipboard, resolving false when the platform has no
 * clipboard API (older browsers / jsdom) so callers can fall back quietly.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (!navigator?.clipboard?.writeText) {
    return false;
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

/**
 * Extracts the backend-provided error message from an axios failure (e.g. a
 * 409 illegal-transition or one-active-config rejection), falling back to the
 * supplied default copy.
 */
export const getApiErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (axios.isAxiosError(error)) {
    const message = (
      error.response?.data as { message?: string | string[] } | undefined
    )?.message;
    if (Array.isArray(message) && message.length > 0) {
      return message.join(', ');
    }
    if (typeof message === 'string' && message.trim() !== '') {
      return message;
    }
  }
  return fallback;
};

/** Triggers a browser download for a blob (used for the recipients CSV). */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * An order's origin, with "absent" read as a mailing.
 *
 * Every ticket written before ingestion existed lacks the field, and those are
 * all member-composed mailings — so the mailing tab claims them rather than
 * them vanishing from both tabs during a deploy skew.
 */
export const originOf = (ticket: { origin?: TicketOrigin }): TicketOrigin =>
  ticket.origin === 'book' ? 'book' : 'mod';

export const isBookTicket = (ticket: { origin?: TicketOrigin }): boolean =>
  originOf(ticket) === 'book';

/**
 * One-line ship-to for a book order. Returns null when there is nothing real to
 * show, so the caller renders the missing-address WARNING rather than an empty
 * line that reads as "no problem here".
 */
export const formatShipTo = (shipTo?: BookShipTo): string | null => {
  if (!shipTo) return null;
  const street = [shipTo.address1, shipTo.address2]
    .filter((part) => Boolean(part && part.trim()))
    .join(', ');
  const region = [shipTo.city, shipTo.state].filter(Boolean).join(', ');
  const line = [street, region, shipTo.zip, shipTo.country]
    .filter((part) => Boolean(part && String(part).trim()))
    .join(' · ');
  return line.trim() === '' ? null : line;
};

/**
 * One line of the ticket's audit panel.
 *
 * Lives here rather than in the page so it can be tested directly: the
 * absent-`from` branch shipped as the literal "undefined → Paid" on every
 * ingested book ticket, and nothing covered it.
 */
const PRINT_FILE_KINDS: readonly TicketFileKind[] = [
  'cover',
  'manuscript',
  'invoice',
];

/** The file kind a print-file history event names, or `undefined`. */
const printFileKindOf = (kind: unknown): TicketFileKind | undefined =>
  PRINT_FILE_KINDS.find((known) => known === kind);

export const historyEventLabel = (event: TicketEvent): string => {
  const metaType =
    typeof event.meta?.type === 'string' ? event.meta.type : undefined;
  // An operator cancel is a real status → cancelled change, but it reads as
  // the decision it was, not as a bare transition. Older records also carry a
  // reason category; a cancel sent without one has none.
  if (metaType === OPERATOR_CANCELLED_EVENT_TYPE) {
    const category = event.meta?.reasonCategory;
    return typeof category === 'string' && category !== ''
      ? formatMessage(
          printShopStrings.detail.history.operatorCancelledWithCategory,
          { category: cancelReasonCategoryLabel(category) },
        )
      : printShopStrings.detail.history.operatorCancelled;
  }
  // A status correction (task 60) is a real from→to change, but should read as
  // a correction, not a normal forward transition.
  if (metaType === 'status_corrected') {
    return formatMessage(printShopStrings.detail.history.corrected, {
      from: statusText(event.from),
      to: statusText(event.to),
    });
  }
  // A print-file replace / restore names the file it changed.
  const fileKind = printFileKindOf(event.meta?.kind);
  if (fileKind && metaType === 'print_file_replaced') {
    return formatMessage(printShopStrings.detail.history.printFileReplaced, {
      kind: printShopStrings.book.fileLabels[fileKind],
    });
  }
  if (fileKind && metaType === 'print_file_restored') {
    return formatMessage(printShopStrings.detail.history.printFileRestored, {
      kind: printShopStrings.book.fileLabels[fileKind],
    });
  }
  if (metaType === 'ship_to_changed' && event.meta?.invoiceRegenerated === true) {
    return printShopStrings.detail.history.shipToChangedInvoiceRegenerated;
  }
  // The Instant Trust Package (itp-mvp): an added or removed file names itself, and the
  // release reads as what moved the order, not as a bare "Waiting for ITP →
  // Paid".
  if (
    metaType === ITP_EVENT_TYPES.fileAdded &&
    typeof event.meta?.fileName === 'string' &&
    event.meta.fileName !== ''
  ) {
    return formatMessage(printShopStrings.detail.history.itpFileAdded, {
      fileName: event.meta.fileName,
    });
  }
  if (
    metaType === ITP_EVENT_TYPES.fileRemoved &&
    typeof event.meta?.fileName === 'string' &&
    event.meta.fileName !== ''
  ) {
    return formatMessage(printShopStrings.detail.history.itpFileRemoved, {
      fileName: event.meta.fileName,
    });
  }
  if (metaType === ITP_EVENT_TYPES.holdReleased) {
    return printShopStrings.detail.history.itpHoldReleased;
  }
  // A same-status edit carrying a `meta.type` (e.g. a `tracking_updated`
  // correction from task 53) reads as its meaning instead of "shipped → shipped".
  if (event.from === event.to && metaType) {
    return (
      printShopStrings.detail.history.metaLabels[metaType] ??
      printShopStrings.detail.history.metaLabelUnknown
    );
  }
  // An ingested book order was CREATED in its status, not transitioned into it,
  // so `from` is legitimately absent (DS makes it optional for exactly this).
  // Without this branch the panel rendered the literal "undefined → Paid".
  if (!event.from) {
    return formatMessage(printShopStrings.detail.history.created, {
      to: statusText(event.to),
    });
  }
  // A normal forward transition: map BOTH ends through statusText so the most
  // visually dominant audit panel reads "In production → Shipped", never the
  // raw snake_case enums "in_production → shipped" (task 5).
  return formatMessage(printShopStrings.detail.history.transition, {
    from: statusText(event.from),
    to: statusText(event.to),
  });
};

/**
 * The lanes before Paid (book-order-approval ADR-001). A ticket in one of them
 * offers no print action, no stepper action and no status correction: the
 * member has not approved the book yet. DS refuses all three as well.
 */
export const APPROVAL_PENDING_STATUSES: readonly ApprovalTicketStatus[] = [
  'awaiting_approval',
  'changes_requested',
];

export const isApprovalPendingStatus = (
  status: string | undefined,
): status is ApprovalTicketStatus =>
  APPROVAL_PENDING_STATUSES.includes(status as ApprovalTicketStatus);

/**
 * The lane before Paid of an order waiting for its Instant Trust Package
 * (itp-mvp). Like the approval lanes: no print action, no status correction.
 */
export const ITP_PENDING_STATUSES: readonly ItpTicketStatus[] = ['itp_pending'];

export const isItpPendingStatus = (
  status: string | undefined,
): status is ItpTicketStatus =>
  ITP_PENDING_STATUSES.includes(status as ItpTicketStatus);

/**
 * Any lane before Paid: waiting for the member's approval or for the ITP.
 * Nothing in the production flow may act on such a ticket yet.
 */
export const isPrePrintStatus = (status: string | undefined): boolean =>
  isApprovalPendingStatus(status) || isItpPendingStatus(status);

/** `meta.type` of the ITP history events DS writes (itp-mvp). */
export const ITP_EVENT_TYPES = {
  fileAdded: 'itp_file_added',
  fileRemoved: 'itp_file_removed',
  markedReady: 'itp_marked_ready',
  holdReleased: 'itp_hold_released',
} as const;

/**
 * Whether the ITP card offers Add, Remove and Mark ITP ready: the ITP is
 * still pending and the order is still held before Paid (DS accepts ITP
 * changes in the approval lanes too). DS re-checks it (`ITP_NOT_EDITABLE`).
 */
export const canEditItp = (ticket: {
  status: string;
  itp?: Pick<TicketItp, 'status'>;
}): boolean => ticket.itp?.status === 'pending' && isPrePrintStatus(ticket.status);

/**
 * "312 KB", "4.1 MB": the size of an ITP file. A size that rounds to 1024 KB
 * reads as "1.0 MB", never "1024 KB".
 */
export const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = Math.round(bytes / 1024);
  if (kb < 1024) return `${kb} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** The first bytes of every PDF. */
const PDF_SIGNATURE = '%PDF-';

/**
 * Whether the file starts with `%PDF-`, read in the browser before anything
 * is sent (DS checks the same signature). A file that cannot be read counts
 * as not a PDF.
 */
export const hasPdfSignature = (file: Blob): Promise<boolean> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const bytes = new Uint8Array(reader.result as ArrayBuffer);
      resolve(String.fromCharCode(...Array.from(bytes)) === PDF_SIGNATURE);
    };
    reader.onerror = () => resolve(false);
    reader.readAsArrayBuffer(file.slice(0, PDF_SIGNATURE.length));
  });

const isItpRefusalCode = (code: unknown): code is ItpRefusalCode =>
  typeof code === 'string' &&
  Object.prototype.hasOwnProperty.call(ITP_REFUSAL_STATUS, code);

/**
 * The DS refusal code of a failed ITP call when it is known and answered with
 * its own status, else null.
 */
export const getItpRefusalCode = (error: unknown): ItpRefusalCode | null => {
  if (!axios.isAxiosError(error)) return null;
  const code = (error.response?.data as { code?: unknown } | undefined)?.code;
  return isItpRefusalCode(code) && ITP_REFUSAL_STATUS[code] === error.response?.status
    ? code
    : null;
};

/** ABO's text for a failed ITP call: its refusal code's text, else the fallback. */
export const itpErrorMessage = (error: unknown, fallback: string): string => {
  const code = getItpRefusalCode(error);
  return code ? printShopStrings.detail.itp.refusals[code] : fallback;
};

/**
 * Whether a failed ITP call leaves the ticket in doubt, so the card refetches
 * it: a 404 or 409 (the file or the order moved on), or an error ABO has no
 * text for. A per-file refusal (not a PDF, too large), a sign-in refusal or
 * the operator check being off changed nothing on the order.
 */
export const itpErrorNeedsTicketRefetch = (error: unknown): boolean => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  if (status === 404 || status === 409) return true;
  return getItpRefusalCode(error) === null;
};

/**
 * A status correction refused because the order is held for its ITP (409
 * `TICKET_STATUS_HELD`). Read from `code`, the field every typed DS refusal
 * uses.
 */
export const isStatusHeldError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response?.status === 409 &&
  (error.response.data as { code?: unknown } | undefined)?.code ===
    TICKET_STATUS_HELD;

/** Tailwind classes for the hero's status pill, per status. */
export const heroStatusPillClass = (status: string | undefined): string => {
  switch (status) {
    case 'shipped':
      return 'bg-success-50 text-success-700';
    case 'awaiting_approval':
      return 'bg-indigo-50 text-indigo-700';
    case 'changes_requested':
      return 'bg-pink-50 text-pink-700';
    case 'itp_pending':
      return 'bg-teal-50 text-teal-700';
    default:
      return 'bg-white text-primary-700';
  }
};

/** Bounds of the manual-approval reason (DS validates the same, 422). */
export const APPROVAL_REASON_MIN_LENGTH = 3;
export const APPROVAL_REASON_MAX_LENGTH = 500;

export type ApprovalReasonError = 'too_short' | 'too_long';

/** Validates a manual-approval reason the way DS does, on the trimmed text. */
export const validateApprovalReason = (
  reason: string,
): ApprovalReasonError | null => {
  const length = reason.trim().length;
  if (length < APPROVAL_REASON_MIN_LENGTH) return 'too_short';
  if (length > APPROVAL_REASON_MAX_LENGTH) return 'too_long';
  return null;
};

/** "The member", "Support (ops@…)" or "Automatically (kill-switch)". */
export const approvalDecidedByLabel = (
  decidedBy: { type: string; id: string } | null | undefined,
): string => {
  if (!decidedBy) return '—';
  const template =
    printShopStrings.detail.approval.actors[
      decidedBy.type as keyof typeof printShopStrings.detail.approval.actors
    ];
  return template
    ? formatMessage(template, { id: decidedBy.id })
    : `${decidedBy.type}:${decidedBy.id}`;
};

/**
 * True when the approval can still be decided by an operator: the ticket sits
 * in an approval lane. The ticket STATUS is the authority — the approval
 * record only describes it.
 */
export const canActOnApproval = (ticket: {
  status: string;
  approval?: TicketApproval;
}): boolean => Boolean(ticket.approval) && isApprovalPendingStatus(ticket.status);

/** `meta.type` of the same-status approval events DS writes (techspec §4). */
export const APPROVAL_EVENT_TYPES = {
  changesRequested: 'approval_changes_requested',
  resent: 'approval_resent',
  revised: 'approval_revised',
  expired: 'approval_expired',
} as const;

const eventMetaType = (event: TicketEvent): string | undefined =>
  typeof event.meta?.type === 'string' ? event.meta.type : undefined;

const isUndecidedApproval = (approval: TicketApproval): boolean =>
  approval.state === 'awaiting' || approval.state === 'changes_requested';

/**
 * Which headline the approval card shows. An approval is split by who decided,
 * because "approved by support" is the auditable override and must not read
 * like the member's own approval. An order cancelled before the member decided
 * is closed: DS keeps its approval `awaiting` (later `expired`), but nothing is
 * open any more.
 */
export const approvalHeadline = (
  approval: TicketApproval,
  ticketStatus?: string,
): ApprovalHeadline => {
  if (ticketStatus === 'cancelled' && isUndecidedApproval(approval)) {
    return 'closed';
  }
  switch (approval.state) {
    case 'awaiting':
      return approval.expired ? 'awaiting_expired' : 'awaiting';
    case 'changes_requested':
      return 'changes_requested';
    case 'bypassed':
      return 'bypassed';
    default:
      if (approval.decidedBy?.type === 'member') return 'approved_by_member';
      if (approval.decidedBy?.type === 'admin') return 'approved_by_support';
      return 'approved';
  }
};

/** The header chip: the state, and for an approval who gave it. */
export const approvalChipLabel = (
  approval: TicketApproval,
  ticketStatus?: string,
): string => {
  const strings = printShopStrings.detail.approval;
  switch (approvalHeadline(approval, ticketStatus)) {
    case 'closed':
      return strings.headlines.closed.title;
    case 'approved_by_member':
      return strings.approvedByMemberChip;
    case 'approved_by_support':
      return strings.approvedBySupportChip;
    default:
      return strings.states[approval.state] ?? approval.state;
  }
};

/**
 * Support approved an order the member had asked to change, and the order was
 * never revised: it prints as it was. Only claimed when the data proves it —
 * still on the first e-mail round and no revision event on record.
 */
export const isApprovedWithoutRevision = (
  ticket: Pick<TicketDetail, 'approval' | 'events'>,
): boolean => {
  const { approval } = ticket;
  if (!approval || approval.state !== 'approved' || !approval.changes) {
    return false;
  }
  const revised = (ticket.events ?? []).some(
    (event) => eventMetaType(event) === APPROVAL_EVENT_TYPES.revised,
  );
  return approval.generation === 1 && !revised;
};

const timeOf = (iso: string | null | undefined): number => {
  const time = iso ? new Date(iso).getTime() : Number.NaN;
  return Number.isNaN(time) ? 0 : time;
};

/**
 * How long after the decision a pending printer e-mail still reads as "being
 * sent". The e-mail is queued at the decision and normally goes out within a
 * minute; pending past this window is the real stuck case.
 */
export const PRINTER_SENDING_WINDOW_MS = 10 * 60 * 1000;

/**
 * How long after the decision the ticket page polls for the printer e-mail
 * to land, so "Sending…" turns into "Sent" without a reload.
 */
export const PRINTER_SENDING_POLL_WINDOW_MS = 2 * 60 * 1000;
export const PRINTER_SENDING_POLL_INTERVAL_MS = 10 * 1000;

/**
 * What the printer release of an approval order also depends on (itp-mvp):
 * the ticket status and its Instant Trust Package. Optional, so a ticket
 * without an ITP reads exactly as before.
 */
export type PrinterReleaseContext = {
  status?: string;
  itp?: Pick<TicketItp, 'readyAt'> | null;
};

/**
 * When the release started: the later of the decision and the ITP being
 * marked ready, since an ITP order is e-mailed to the printer only once both
 * happened. `null` when neither did.
 */
const releaseStartedAt = (
  decidedAt: string | null | undefined,
  itpReadyAt: string | null | undefined,
): number | null => {
  const started = Math.max(timeOf(decidedAt), timeOf(itpReadyAt));
  return started > 0 ? started : null;
};

/**
 * The printer e-mail of an approval waits for the order's ITP (itp-mvp): DS
 * says `held`, or (a DS older than the release visibility) the order still
 * sits in Waiting for ITP.
 */
export const isPrinterHeldForItp = (
  approval: Pick<TicketApproval, 'printer'> | undefined | null,
  context: PrinterReleaseContext = {},
): boolean =>
  approval?.printer?.status === 'held' || isItpPendingStatus(context.status);

/**
 * The order was just released and its New Order e-mail to the printer is
 * queued, not stuck: pending, and released less than ten minutes ago. An
 * ITP order is released by the later of the decision and the ITP being
 * marked ready; while it is held for its ITP it is never "sending".
 */
export const isPrinterSending = (
  approval: TicketApproval | undefined | null,
  now: number = Date.now(),
  context: PrinterReleaseContext = {},
): boolean => {
  if (!approval || isUndecidedApproval(approval)) return false;
  if (approval.printer?.status !== 'pending') return false;
  if (isPrinterHeldForItp(approval, context)) return false;
  if (!approval.decidedAt) return false;
  const started = releaseStartedAt(approval.decidedAt, context.itp?.readyAt);
  return started !== null && now - started < PRINTER_SENDING_WINDOW_MS;
};

/**
 * The ITP card's printer line is "Sending…": the release of an ITP order is
 * pending and the ITP was marked ready less than ten minutes ago.
 */
export const isItpPrinterSending = (
  itp: Pick<TicketItp, 'readyAt' | 'printer'> | undefined | null,
  now: number = Date.now(),
): boolean => {
  if (itp?.printer?.status !== 'pending' || !itp.readyAt) return false;
  return now - timeOf(itp.readyAt) < PRINTER_SENDING_WINDOW_MS;
};

/**
 * The ticket query's refetch interval: every 10 s while the printer e-mail is
 * being sent, for at most two minutes after the release; otherwise off.
 */
export const printerSendingRefetchInterval = (
  ticket: (Pick<TicketDetail, 'approval'> & PrinterReleaseContext) | undefined,
  now: number = Date.now(),
): number | false => {
  if (!ticket) return false;
  const { approval, itp } = ticket;
  const context = { status: ticket.status, itp };
  let started: number | null = null;
  if (approval && isPrinterSending(approval, now, context)) {
    started = releaseStartedAt(approval.decidedAt, itp?.readyAt);
  } else if (isItpPrinterSending(itp, now)) {
    started = releaseStartedAt(null, itp?.readyAt);
  }
  return started !== null && now - started < PRINTER_SENDING_POLL_WINDOW_MS
    ? PRINTER_SENDING_POLL_INTERVAL_MS
    : false;
};

/**
 * The approval story in time order, for the card's conversation column.
 *
 * Sources: the approval record (request, reminder, latest comment, decision,
 * printer e-mail) and the ticket's same-status approval events (re-sends,
 * revisions, expiries, earlier change requests). Nothing is inferred beyond
 * them — a delivery the API has no time for is shown without one, and round
 * 1's delivery is claimed only while its request is still the latest e-mail.
 */
export const buildApprovalConversation = (
  ticket: Pick<TicketDetail, 'approval' | 'events' | 'member'> &
    PrinterReleaseContext,
  now: number = Date.now(),
): ApprovalConversationEntry[] => {
  const { approval } = ticket;
  if (!approval) return [];
  const release: PrinterReleaseContext = { status: ticket.status, itp: ticket.itp };
  const strings = printShopStrings.detail.approval.conversation;
  const events = ticket.events ?? [];
  const timed: Array<{ entry: ApprovalConversationEntry; sortAt: number }> =
    [];
  const trailing: ApprovalConversationEntry[] = [];
  const push = (entry: ApprovalConversationEntry, sortAt: string | null) =>
    timed.push({ entry, sortAt: timeOf(sortAt) });
  const line = (
    id: string,
    text: string,
    at: string | null,
    icon: Extract<ApprovalConversationEntry, { kind: 'system' }>['icon'],
    tone: Extract<ApprovalConversationEntry, { kind: 'system' }>['tone'] = 'neutral',
  ): ApprovalConversationEntry => ({ kind: 'system', id, text, at, icon, tone });

  const undecided = isUndecidedApproval(approval);
  const { lastEmail } = approval;

  if (approval.state !== 'bypassed') {
    const restarts = events.filter((event) => {
      const type = eventMetaType(event);
      return (
        type === APPROVAL_EVENT_TYPES.resent ||
        type === APPROVAL_EVENT_TYPES.revised
      );
    });
    const roundStart =
      restarts[restarts.length - 1]?.createdAt ?? approval.requestedAt;

    // The request. Its delivery is known only for round 1 while the request
    // is still the latest e-mail.
    if (approval.generation === 1 && lastEmail?.kind === 'request') {
      if (lastEmail.status === 'sent') {
        push(
          line('request', strings.requestSent, lastEmail.at ?? approval.requestedAt, 'mail'),
          approval.requestedAt,
        );
      } else if (lastEmail.status === 'failed') {
        push(
          line('request', strings.requestFailed, approval.requestedAt, 'alert', 'danger'),
          approval.requestedAt,
        );
      } else {
        push(
          line('request', strings.requestSending, approval.requestedAt, 'mail'),
          approval.requestedAt,
        );
      }
    } else {
      push(
        line('request', strings.requested, approval.requestedAt, 'mail'),
        approval.requestedAt,
      );
    }

    // Re-sends and revisions start a new round with a new link.
    restarts.forEach((event, index) => {
      const revised = eventMetaType(event) === APPROVAL_EVENT_TYPES.revised;
      push(
        line(
          `restart-${index}`,
          revised
            ? strings.revised
            : formatMessage(strings.resent, {
                actor: approvalDecidedByLabel(event.actor),
              }),
          event.createdAt,
          revised ? 'edit' : 'refresh',
        ),
        event.createdAt,
      );
    });

    // The current round's request, after a re-send or revision.
    if (approval.generation > 1 && lastEmail?.kind === 'request') {
      if (lastEmail.status === 'sent') {
        // Never before the round it belongs to, whatever the clocks say.
        timed.push({
          entry: line('new-request', strings.newRequestSent, lastEmail.at ?? roundStart, 'mail'),
          sortAt: Math.max(timeOf(lastEmail.at), timeOf(roundStart)),
        });
      } else if (lastEmail.status === 'failed') {
        push(
          line('new-request', strings.newRequestFailed, null, 'alert', 'danger'),
          roundStart,
        );
      } else {
        push(
          line('new-request', strings.newRequestSending, null, 'mail'),
          roundStart,
        );
      }
    }

    // The single reminder of the current round.
    if (approval.reminderSentAt) {
      push(
        line('reminder', strings.reminderSent, approval.reminderSentAt, 'bell'),
        approval.reminderSentAt,
      );
    } else if (
      approval.state === 'awaiting' &&
      lastEmail?.kind === 'reminder' &&
      lastEmail.status !== 'sent'
    ) {
      // No time on record for an undelivered reminder: it is the latest
      // thing that happened, so it goes last.
      timed.push({
        entry:
          lastEmail.status === 'failed'
            ? line('reminder', strings.reminderFailed, null, 'alert', 'danger')
            : line('reminder', strings.reminderSending, null, 'bell'),
        sortAt: Number.MAX_SAFE_INTEGER,
      });
    }

    // Expired links: the recorded events, plus the current link if it has
    // expired and the expiry job has not written its event yet.
    const expiries = events.filter(
      (event) => eventMetaType(event) === APPROVAL_EVENT_TYPES.expired,
    );
    expiries.forEach((event, index) =>
      push(
        line(`expired-${index}`, strings.expired, event.createdAt, 'clock'),
        event.createdAt,
      ),
    );
    const currentExpiryRecorded = expiries.some(
      (event) => timeOf(event.createdAt) >= timeOf(roundStart),
    );
    if (approval.expired && undecided && !currentExpiryRecorded) {
      push(
        line('expired-current', strings.expired, approval.expiresAt, 'clock'),
        approval.expiresAt,
      );
    }

    // Change requests: the latest one with its comment, earlier ones as lines.
    const changeEvents = events.filter(
      (event) => eventMetaType(event) === APPROVAL_EVENT_TYPES.changesRequested,
    );
    const earlierChanges = approval.changes
      ? changeEvents.slice(0, -1)
      : changeEvents;
    earlierChanges.forEach((event, index) =>
      push(
        line(`changes-${index}`, strings.earlierChanges, event.createdAt, 'edit'),
        event.createdAt,
      ),
    );
    if (approval.changes) {
      push(
        {
          kind: 'member_comment',
          id: 'changes',
          author: ticket.member?.name?.trim() || strings.memberFallback,
          label: strings.memberCommentKind,
          at: approval.changes.requestedAt,
          comment: approval.changes.comment,
        },
        approval.changes.requestedAt,
      );
    }
  }

  // The decision.
  if (approval.state === 'bypassed') {
    const at = approval.decidedAt ?? approval.requestedAt;
    // An ITP order waits for its ITP; it never went straight to Paid.
    const text =
      ticket.itp || isPrinterHeldForItp(approval, release)
        ? strings.bypassedItp
        : strings.bypassed;
    push(line('decision', text, at, 'slash'), at);
  } else if (approval.state === 'approved') {
    const actor = approvalDecidedByLabel(approval.decidedBy);
    if (approval.decidedBy?.type === 'admin' && approval.approvalReason) {
      push(
        {
          kind: 'support_reason',
          id: 'decision',
          author: actor,
          label: strings.supportReasonKind,
          at: approval.decidedAt,
          reason: approval.approvalReason,
        },
        approval.decidedAt,
      );
    } else {
      const text =
        approval.decidedBy?.type === 'member'
          ? strings.memberApproved
          : approval.decidedBy?.type === 'admin'
            ? formatMessage(strings.supportApproved, { actor })
            : formatMessage(strings.otherApproved, { actor });
      push(
        line('decision', text, approval.decidedAt, 'check', 'success'),
        approval.decidedAt,
      );
    }
  }

  // The printer's New Order e-mail, released only after the decision.
  if (!undecided && isPrinterHeldForItp(approval, release)) {
    trailing.push({ kind: 'pending', id: 'printer', text: strings.printerHeld });
  } else if (!undecided) {
    const decidedAt = approval.decidedAt ?? approval.requestedAt;
    switch (approval.printer?.status) {
      case 'sent':
        push(
          line('printer', strings.printerSent, approval.printer.at ?? decidedAt, 'printer', 'success'),
          approval.printer.at ?? decidedAt,
        );
        break;
      case 'failed':
        push(
          line('printer', strings.printerFailed, null, 'alert', 'danger'),
          decidedAt,
        );
        break;
      case 'pending':
        trailing.push({
          kind: 'pending',
          id: 'printer',
          text: isPrinterSending(approval, now, release)
            ? strings.printerSending
            : strings.printerPending,
        });
        break;
      default:
        break;
    }
  }

  if (approval.state === 'awaiting') {
    trailing.push({ kind: 'pending', id: 'next', text: strings.noReply });
  } else if (approval.state === 'changes_requested') {
    trailing.push({ kind: 'pending', id: 'next', text: strings.noDecision });
  }

  // Array.prototype.sort is stable: same-time lines keep the order above.
  return [
    ...timed.sort((a, b) => a.sortAt - b.sortAt).map(({ entry }) => entry),
    ...trailing,
  ];
};

/** `meta.type` of the history event DS writes for an operator cancel. */
export const OPERATOR_CANCELLED_EVENT_TYPE = 'operator_cancelled';

/** `meta.type` of the single history event of a historical import. */
export const BOOK_ORDER_IMPORTED_EVENT_TYPE = 'book_order_imported';

/** The label of a cancel reason category, falling back to the raw value. */
export const cancelReasonCategoryLabel = (category: string): string =>
  (printShopStrings.detail.cancel.reasonCategories as Record<string, string>)[
    category
  ] ?? category;

/**
 * True for a book order copied in by the historical import, whatever status
 * it landed in (Paid included). The ticket detail carries no import flag, so
 * this reads the import's own history event. DS refuses the cancel anyway
 * (422 `BOOK_CANCEL_IMPORTED`); this only keeps the button off such tickets.
 */
export const isImportedBookTicket = (ticket: {
  events?: TicketEvent[];
}): boolean =>
  (ticket.events ?? []).some(
    (event) => event.meta?.type === BOOK_ORDER_IMPORTED_EVENT_TYPE,
  );

/**
 * The statuses an operator may cancel a book order from (DS mirrors it): every
 * live status, Shipped included, since a print-shop mistake found after
 * shipping is the print shop's to refund.
 */
export const BOOK_CANCELLABLE_STATUSES: readonly TicketStatus[] = [
  'awaiting_approval',
  'changes_requested',
  'itp_pending',
  'paid',
  'accepted',
  'in_production',
  'shipped',
];

/**
 * Whether the "Cancel order" action is offered: a live (not imported) book
 * order in any status above. The role check is the caller's; DS re-checks all
 * of it.
 */
export const canOfferBookCancel = (
  ticket: Pick<TicketDetail, 'origin' | 'status' | 'events'>,
): boolean =>
  isBookTicket(ticket) &&
  (BOOK_CANCELLABLE_STATUSES as readonly string[]).includes(ticket.status) &&
  !isImportedBookTicket(ticket);

/**
 * A ticket an ABO operator cancelled. Only a `cancelled` status counts: an
 * undone cancel (back to Paid by the runbook) keeps its record but is not
 * shown as cancelled.
 */
export const isOperatorCancelledTicket = (
  ticket: Pick<TicketDetail, 'status' | 'cancellation'>,
): boolean => ticket.status === 'cancelled' && Boolean(ticket.cancellation);

/**
 * Whether "Return credits" may apply: an operator-cancelled ticket with no
 * refund yet, cancelled without a ledger refund (a `deferred` cancel, or a
 * `none` cancel whose debit landed late). The settle preview decides.
 */
export const mayHaveRefundToSettle = (
  ticket: Pick<TicketDetail, 'status' | 'cancellation'>,
): boolean =>
  isOperatorCancelledTicket(ticket) &&
  !ticket.cancellation?.refund &&
  ticket.cancellation?.refundMode !== 'ledger';

/**
 * The stepper of an operator-cancelled ticket: every milestone up to the
 * status the order was cancelled from is done (only Submitted for an approval
 * lane, which the stepper does not draw), then a cancelled marker. An ITP
 * order keeps its Waiting for ITP milestone.
 */
export type CancelledStep = TicketStep | 'cancelled';

export const cancelledStepStates = (
  fromStatus: TicketCancellation['fromStatus'] = 'paid',
  includesItp = false,
): {
  step: CancelledStep;
  state: StepState | 'cancelled';
}[] => {
  const order: readonly TicketStep[] =
    includesItp || fromStatus === 'itp_pending'
      ? ITP_TICKET_STEP_ORDER
      : TICKET_STEP_ORDER;
  const reached = Math.max(order.indexOf(fromStatus as TicketStep), 0);
  return [
    ...order
      .slice(0, reached + 1)
      .map((step) => ({ step, state: 'done' as const })),
    { step: 'cancelled', state: 'cancelled' },
  ];
};

/** A ledger option that returns the order's own debit, with its amount. */
export type DebitRefundOption = BookRefundOption & {
  mode: 'ledger';
  amountSource: 'debit';
  amount: number;
};

export const isDebitRefundOption = (
  option: BookRefundOption,
): option is DebitRefundOption =>
  option.mode === 'ledger' &&
  option.amountSource === 'debit' &&
  typeof option.amount === 'number';

/**
 * The option behind the cancel dialog's "Return the credits" checkbox, or
 * null when there is no checkbox: `deferred` (before the credits cutover), else
 * a `ledger` debit option when the operator may adjust credits. A ledger
 * option with an operator-typed amount is never offered here; engineering
 * handles it through the API.
 */
export const returnableRefundOption = (
  options: BookRefundOption[],
  canAdjustCredits: boolean,
): BookRefundOption | null => {
  const deferred = options.find((option) => option.mode === 'deferred');
  if (deferred) return deferred;
  const debit = options.find(isDebitRefundOption);
  return debit && canAdjustCredits ? debit : null;
};

/** The debit option "Return credits" offers on a settle preview, or null. */
export const settleDebitOption = (
  preview: BookCancelPreview | undefined,
): DebitRefundOption | null =>
  preview?.kind === 'settle' && preview.eligible
    ? (preview.refundOptions.find(isDebitRefundOption) ?? null)
    : null;

/** "60 copies · member@x.com" under the cancel dialog title, missing parts left out. */
export const cancelDialogSubtitle = (
  preview: Pick<BookCancelPreview, 'copies' | 'memberEmail'>,
): string =>
  [
    typeof preview.copies === 'number'
      ? printShopStrings.book.copiesUnit(preview.copies)
      : null,
    preview.memberEmail,
  ]
    .filter((part): part is string => Boolean(part))
    .join(' · ');

const isBookCancelRefusalCode = (
  code: unknown,
): code is BookCancelRefusalCode =>
  typeof code === 'string' &&
  Object.prototype.hasOwnProperty.call(BOOK_CANCEL_REFUSAL_STATUS, code);

/**
 * The typed refusal behind a failed cancel call, or `null` for any other
 * failure (a validation 422 carries no code). Recognised only when the code
 * is known AND the status is the one DS answers it with. Structural on
 * purpose: it reads the axios error shape without `axios.isAxiosError`.
 */
export const getBookCancelRefusal = (
  error: unknown,
): BookCancelRefusal | null => {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const response = (error as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null) {
    return null;
  }
  const { status, data } = response as { status?: unknown; data?: unknown };
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  const { code } = data as { code?: unknown };
  if (
    !isBookCancelRefusalCode(code) ||
    BOOK_CANCEL_REFUSAL_STATUS[code] !== status
  ) {
    return null;
  }
  return { code, status: BOOK_CANCEL_REFUSAL_STATUS[code] };
};

/**
 * The text of a DS 4xx error body, or the fallback. DS answers every error
 * through its global exception filter: `{statusCode, error, errors: [...],
 * ...}` and never a `message`, so `errors` is read first (`message` stays as
 * a fallback for other servers). A 5xx always gets the fallback: its
 * `errors` carry the raw internal error text.
 */
export const getDsErrorMessage = (error: unknown, fallback: string): string => {
  if (!axios.isAxiosError(error)) {
    return fallback;
  }
  const status = error.response?.status;
  if (status === undefined || status >= 500) {
    return fallback;
  }
  const errors = (error.response?.data as { errors?: unknown } | undefined)
    ?.errors;
  const texts = Array.isArray(errors)
    ? errors.filter(
        (text): text is string =>
          typeof text === 'string' && text.trim() !== '',
      )
    : [];
  return texts.length > 0
    ? texts.join(', ')
    : getApiErrorMessage(error, fallback);
};

/**
 * What a failed cancel or "Return credits" call means for the dialog.
 *
 * - `close`: the ticket moved on (409) or is gone (404). Retrying the same
 *   input cannot succeed, so the dialog closes and the page refetches.
 * - otherwise the dialog stays open with the operator's input: a sign-in
 *   problem (401/403), DS unavailable (503), a refused input (422) or an
 *   unexpected failure (5xx, network). Resending the same request after a
 *   5xx completes it; DS never returns credits twice. A recognised 422
 *   refusal shows ABO's own text for its code; any other failure shows the
 *   DS error text, or the fallback.
 */
export type BookCancelFailureKind =
  | 'conflict'
  | 'not_found'
  | 'unverified'
  | 'forbidden'
  | 'unavailable'
  | 'error';

export interface BookCancelFailure {
  kind: BookCancelFailureKind;
  close: boolean;
  message: string;
}

const CLOSING_CONFLICT_MESSAGES: Partial<
  Record<BookCancelRefusalCode, string>
> = {
  BOOK_CANCEL_NOT_SETTLEABLE:
    printShopStrings.detail.cancel.blocked.BOOK_CANCEL_NOT_SETTLEABLE,
};

/** ABO's own text for each 422 refusal (the dialog stays open). */
const REFUSED_INPUT_MESSAGES: Partial<Record<BookCancelRefusalCode, string>> = {
  BOOK_CANCEL_NOT_BOOK_ORDER:
    printShopStrings.detail.cancel.blocked.BOOK_CANCEL_NOT_BOOK_ORDER,
  BOOK_CANCEL_IMPORTED:
    printShopStrings.detail.cancel.blocked.BOOK_CANCEL_IMPORTED,
};

export const classifyBookCancelFailure = (
  error: unknown,
  fallback: string,
): BookCancelFailure => {
  const strings = printShopStrings.detail.cancel;
  const refusal = getBookCancelRefusal(error);
  const status =
    refusal?.status ??
    (axios.isAxiosError(error) ? error.response?.status : undefined);
  if (status === 409) {
    return {
      kind: 'conflict',
      close: true,
      message:
        (refusal && CLOSING_CONFLICT_MESSAGES[refusal.code]) ??
        strings.conflict,
    };
  }
  if (status === 404) {
    return { kind: 'not_found', close: true, message: strings.notFound };
  }
  if (refusal?.code === 'BOOK_CANCEL_OPERATOR_UNVERIFIED') {
    return { kind: 'unverified', close: false, message: strings.unverified };
  }
  if (refusal?.code === 'BOOK_CANCEL_FORBIDDEN') {
    return { kind: 'forbidden', close: false, message: strings.forbidden };
  }
  if (refusal?.code === 'BOOK_CANCEL_UNAVAILABLE') {
    return { kind: 'unavailable', close: false, message: strings.unavailable };
  }
  return {
    kind: 'error',
    close: false,
    message:
      (refusal && REFUSED_INPUT_MESSAGES[refusal.code]) ??
      getDsErrorMessage(error, fallback),
  };
};

/** The short credits outcome on the cancelled ticket's banner line. */
export const cancellationCreditsText = (
  cancellation: TicketCancellation,
): string => {
  const credits = printShopStrings.detail.cancel.banner.credits;
  const { refund } = cancellation;
  if (refund) {
    if (refund.outcome === 'nothing_owed') return credits.nothingOwed;
    return formatMessage(refund.settledBy ? credits.settled : credits.refunded, {
      amount: String(refund.amount),
      date: formatDate(refund.at),
    });
  }
  if (cancellation.refundMode === 'deferred') return credits.deferred;
  if (cancellation.refundMode === 'ledger') return credits.ledgerPending;
  return credits.none;
};

/**
 * A `ledger` cancel whose refund never completed. Rare and actionable: the
 * banner adds a line asking to report it to engineering.
 */
export const isLedgerRefundPending = (
  cancellation: TicketCancellation,
): boolean => cancellation.refundMode === 'ledger' && !cancellation.refund;

/** The largest print file DS accepts (DS `PRINT_FILE_MAX_BYTES`). */
export const PRINT_FILE_MAX_BYTES = 100 * 1024 * 1024;

/**
 * Why a picked file cannot be uploaded, checked in the browser before anything
 * is sent, or `null` when it may go. DS checks the same again (and the PDF
 * signature), so this only spares the operator a 100 MB round trip.
 */
export const printFileClientError = (
  file: Pick<File, 'name' | 'type' | 'size'>,
): string | null => {
  const isPdf =
    file.type === 'application/pdf' ||
    (file.type === '' && file.name.toLowerCase().endsWith('.pdf'));
  if (!isPdf) {
    return printShopStrings.book.fileNotPdf;
  }
  if (file.size > PRINT_FILE_MAX_BYTES) {
    return printShopStrings.book.fileTooLarge;
  }
  return null;
};

const isPrintFileRefusalCode = (code: unknown): code is PrintFileRefusalCode =>
  typeof code === 'string' &&
  Object.prototype.hasOwnProperty.call(PRINT_FILE_REFUSAL_STATUS, code);

/**
 * ABO's text for a failed replace / restore: the text of its DS refusal code
 * when the code is known and answered with its own status, else the fallback.
 */
export const printFileErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (!axios.isAxiosError(error)) {
    return fallback;
  }
  const code = (error.response?.data as { code?: unknown } | undefined)?.code;
  if (
    !isPrintFileRefusalCode(code) ||
    PRINT_FILE_REFUSAL_STATUS[code] !== error.response?.status
  ) {
    return fallback;
  }
  return printShopStrings.book.fileRefusals[code];
};

/**
 * ABO's text for a failed flip book replace. A bare 504 (the load balancer
 * gave up before DS answered) means the same as DS's own
 * `FLIPBOOK_REPLACE_TIMEOUT`: the flip book may still be updating.
 */
export const flipBookReplaceErrorMessage = (error: unknown): string => {
  const timedOut =
    axios.isAxiosError(error) &&
    error.response?.status === PRINT_FILE_REFUSAL_STATUS.FLIPBOOK_REPLACE_TIMEOUT;
  return printFileErrorMessage(
    error,
    timedOut
      ? printShopStrings.book.fileRefusals.FLIPBOOK_REPLACE_TIMEOUT
      : printShopStrings.book.flipBookReplaceFailed,
  );
};

/**
 * Whether DS refused the request because the operator's sign-in could not be
 * verified (an expired or missing id token): the answer is "Sign in again".
 */
export const isOperatorUnverified = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response?.status === PRINT_FILE_REFUSAL_STATUS.ABO_OPERATOR_UNVERIFIED &&
  (error.response.data as { code?: unknown } | undefined)?.code ===
    'ABO_OPERATOR_UNVERIFIED';

/**
 * The message for a failed ship-to save: ABO's text for `SHIP_TO_INVALID`,
 * else the print-file refusal text (order closed, not a book), else the
 * fallback.
 */
export const shipToErrorMessage = (error: unknown): string => {
  if (
    axios.isAxiosError(error) &&
    error.response?.status === 400 &&
    (error.response.data as { code?: unknown } | undefined)?.code ===
      SHIP_TO_INVALID
  ) {
    return printShopStrings.book.shipToInvalid;
  }
  return printFileErrorMessage(error, printShopStrings.book.shipToSaveFailed);
};
